import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Fade
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Mic as MicIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
  School as AcademicIcon,
  Image as ImageIcon,
  AutoStories as StoryIcon,
  Create as MakerIcon,
  Category as CategoryIcon,
  ArrowForward as ArrowIcon,
  BrokenImage as BrokenImageIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

// Separate component for search image thumbnails with safe error handling
const SearchImageThumbnail = ({ img, onClick }) => {
  const [hasError, setHasError] = useState(false);
  const imgSrc = img.thumbnail || img.path || '';
  
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        width: 80,
        textAlign: 'center',
        '&:hover': { opacity: 0.8 }
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 60,
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5
        }}
      >
        {hasError ? (
          <BrokenImageIcon sx={{ color: '#bdbdbd', fontSize: 30 }} />
        ) : (
          <img
            src={imgSrc}
            alt={img.title || 'Image'}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'cover'
            }}
            onError={() => setHasError(true)}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: '10px',
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: '#333'
        }}
      >
        {img.title || 'Image'}
      </Typography>
    </Box>
  );
};

// One Click Resource Center (OCRC) categories - PRIORITY SEARCH
// These map search terms to OCRC Image Bank pages
const OCRC_CATEGORIES = {
  // Animals section
  'animals': { path: '/views/academic/imagebank/animals', mu: 0 },
  'animal': { path: '/views/academic/imagebank/animals', mu: 0 },
  'domestic animals': { path: '/views/academic/imagebank/animals/domestic-animals', mu: 0 },
  'wild animals': { path: '/views/academic/imagebank/animals/wild-animals', mu: 0 },
  'sea animals': { path: '/views/academic/imagebank/animals/sea-animals', mu: 0 },
  // Birds
  'birds': { path: '/views/academic/imagebank/birds', mu: 1 },
  'bird': { path: '/views/academic/imagebank/birds', mu: 1 },
  // Flowers
  'flowers': { path: '/views/academic/imagebank/flowers', mu: 2 },
  'flower': { path: '/views/academic/imagebank/flowers', mu: 2 },
  // Fruits
  'fruits': { path: '/views/academic/imagebank/fruits', mu: 3 },
  'fruit': { path: '/views/academic/imagebank/fruits', mu: 3 },
  // Vegetables
  'vegetables': { path: '/views/academic/imagebank/vegetables', mu: 4 },
  'vegetable': { path: '/views/academic/imagebank/vegetables', mu: 4 },
  // Plants
  'plants': { path: '/views/academic/imagebank/plants', mu: 5 },
  'plant': { path: '/views/academic/imagebank/plants', mu: 5 },
  // Insects
  'insects': { path: '/views/academic/imagebank/insects', mu: 6 },
  'insect': { path: '/views/academic/imagebank/insects', mu: 6 },
  // Professions
  'professions': { path: '/views/academic/imagebank/professions', mu: 7 },
  'profession': { path: '/views/academic/imagebank/professions', mu: 7 },
  // Great Personalities
  'great personalities': { path: '/views/academic/imagebank/great-personalities', mu: 8 },
  'personalities': { path: '/views/academic/imagebank/great-personalities', mu: 8 },
  'scientists': { path: '/views/academic/imagebank/great-personalities', mu: 8 },
  'freedom fighters': { path: '/views/sections/imagebank/freedom-fighters', mu: 0 },
  // House Hold Things
  'household': { path: '/views/academic/imagebank/house-hold-things', mu: 9 },
  'house hold': { path: '/views/academic/imagebank/house-hold-things', mu: 9 },
  // Sections - Comics, Rhymes etc
  'comics': { path: '/views/sections/comics', mu: 8 },
  'comic': { path: '/views/sections/comics', mu: 8 },
  'rhymes': { path: '/views/sections/rhymes', mu: 1 },
  'rhyme': { path: '/views/sections/rhymes', mu: 1 },
  'stories': { path: '/views/sections/pictorial-stories', mu: 2 },
  'moral stories': { path: '/views/sections/pictorial-stories', mu: 2 },
  'pictorial stories': { path: '/views/sections/pictorial-stories', mu: 2 },
  // Other OCRC sections
  'festivals': { path: '/views/sections/imagebank/festivals', mu: 0 },
  'festival': { path: '/views/sections/imagebank/festivals', mu: 0 },
  'good manners': { path: '/views/sections/imagebank/good-manners', mu: 0 },
  'manners': { path: '/views/sections/imagebank/good-manners', mu: 0 },
  'body parts': { path: '/views/sections/imagebank/our-body-parts', mu: 0 },
  'five senses': { path: '/views/sections/imagebank/five-senses', mu: 0 },
  'senses': { path: '/views/sections/imagebank/five-senses', mu: 0 },
  'vehicles': { path: '/views/sections/imagebank/vehicles', mu: 0 },
  'vehicle': { path: '/views/sections/imagebank/vehicles', mu: 0 },
  'opposites': { path: '/views/sections/imagebank/opposites', mu: 0 },
  'expressions': { path: '/views/sections/imagebank/expressions', mu: 0 },
  'expression': { path: '/views/sections/imagebank/expressions', mu: 0 },
  'habits': { path: '/views/sections/imagebank/habits', mu: 0 },
  'habit': { path: '/views/sections/imagebank/habits', mu: 0 },
  'safety': { path: '/views/sections/safety', mu: 0 },
  'road safety': { path: '/views/sections/safety/road-safety', mu: 0 },
  'first aid': { path: '/views/sections/imagebank/first-aid', mu: 0 },
  'pollution': { path: '/views/sections/imagebank/pollution', mu: 0 },
  // Value Education
  'values': { path: '/views/sections/value-education', mu: 0 },
  'value education': { path: '/views/sections/value-education', mu: 0 },
  'life skills': { path: '/views/sections/value-education/life-skills', mu: 0 },
  // GK & Science
  'experiments': { path: '/views/sections/gk-science/experiments', mu: 0 },
  'experiment': { path: '/views/sections/gk-science/experiments', mu: 0 },
  'amazing facts': { path: '/views/sections/gk-science/amazing-facts', mu: 0 },
  'facts': { path: '/views/sections/gk-science/amazing-facts', mu: 0 },
  // Flash Cards
  'flash cards': { path: '/views/sections/flash-cards', mu: 0 },
  'flashcards': { path: '/views/sections/flash-cards', mu: 0 },
  // Puzzles
  'puzzles': { path: '/views/sections/puzzles-riddles', mu: 0 },
  'puzzle': { path: '/views/sections/puzzles-riddles', mu: 0 },
  'riddles': { path: '/views/sections/puzzles-riddles', mu: 0 },
  'riddle': { path: '/views/sections/puzzles-riddles', mu: 0 },
};

// Specific animal keywords that should go to Animals OCRC
const ANIMAL_KEYWORDS_TO_OCRC = [
  'monkey', 'dog', 'cat', 'elephant', 'lion', 'tiger', 'cow', 'horse', 'rabbit', 'bear', 
  'deer', 'giraffe', 'zebra', 'snake', 'frog', 'camel', 'goat', 'sheep', 'pig', 'fox', 
  'wolf', 'cheetah', 'leopard', 'panda', 'koala', 'kangaroo', 'crocodile', 'alligator', 
  'turtle', 'tortoise', 'dolphin', 'whale', 'shark', 'octopus', 'crab', 'lobster',
  'rhinoceros', 'hippopotamus', 'gorilla', 'chimpanzee', 'orangutan', 'buffalo'
];

// Bird keywords that should go to Birds OCRC
const BIRD_KEYWORDS_TO_OCRC = [
  'parrot', 'peacock', 'sparrow', 'crow', 'eagle', 'owl', 'pigeon', 'duck', 'hen', 
  'rooster', 'vulture', 'penguin', 'flamingo', 'swan', 'kingfisher', 'woodpecker'
];

// Insect keywords
const INSECT_KEYWORDS_TO_OCRC = [
  'butterfly', 'bee', 'ant', 'spider', 'scorpion', 'grasshopper', 'dragonfly', 
  'ladybug', 'mosquito', 'fly', 'cockroach', 'beetle', 'caterpillar', 'worm'
];

// Fish keywords (sea animals)
const FISH_KEYWORDS_TO_OCRC = [
  'fish', 'fishes', 'goldfish', 'shark', 'whale', 'dolphin', 'octopus', 'jellyfish',
  'starfish', 'seahorse', 'crab', 'lobster', 'shrimp', 'clam', 'oyster'
];

// Knowledge base for smart suggestions
const KNOWLEDGE_BASE = {
  categories: [
    { name: 'Academic', icon: <AcademicIcon />, path: '/views/academic', color: '#2196f3', keywords: ['class', 'grade', 'subject', 'math', 'science', 'english', 'hindi'] },
    { name: 'Image Bank', icon: <ImageIcon />, path: '/views/academic/imagebank', color: '#4caf50', keywords: ['image', 'picture', 'photo', 'animal', 'object'] },
    { name: 'Makers', icon: <MakerIcon />, path: '/views/maker/makers', color: '#ff9800', keywords: ['chart', 'worksheet', 'story', 'lesson', 'scheduler'] },
    { name: 'Early Career', icon: <StoryIcon />, path: '/views/early-career', color: '#9c27b0', keywords: ['story', 'career', 'moral', 'value'] },
  ],
  quickLinks: [
    { name: 'Smart Wall', path: '/views/academic/smart-wall?ocrc', category: 'Resources' },
    { name: 'Chart Maker', path: '/views/maker/makers/chart-maker?main=0&mu=0', category: 'Makers' },
    { name: 'Worksheet Maker', path: '/views/maker/makers/worksheet-maker', category: 'Makers' },
    { name: 'Story Maker', path: '/views/maker/makers/story-maker', category: 'Makers' },
    { name: 'Micro Scheduler', path: '/views/maker/makers/micro-scheduler', category: 'Makers' },
    { name: 'MCQ Bank', path: '/views/academic/result?text=mcq', category: 'Resources' },
    { name: 'Exam Tips', path: '/views/academic/result?text=exam tips', category: 'Resources' },
    { name: 'Great Personalities', path: '/views/academic/imagebank/great-personalities', category: 'Content' },
    { name: 'Comics', path: '/views/sections/comics', category: 'Content' },
    { name: 'Discovery', path: '/views/sections/imagebank/discovery', category: 'Content' },
  ],
  animalKeywords: ANIMAL_KEYWORDS_TO_OCRC,
  birdKeywords: BIRD_KEYWORDS_TO_OCRC,
  insectKeywords: INSECT_KEYWORDS_TO_OCRC,
  fishKeywords: FISH_KEYWORDS_TO_OCRC,
  objectKeywords: ['tree', 'mountain', 'river', 'sun', 'moon', 'star', 'cloud', 'rain', 'house', 'car', 'bus', 'train', 'boat', 'ball', 'toy', 'book', 'pencil', 'chair', 'bicycle', 'airplane', 'computer', 'phone', 'table', 'bed', 'clock', 'lamp', 'fan', 'door', 'window', 'bridge', 'road', 'building', 'school', 'hospital', 'park', 'garden', 'forest', 'beach', 'ocean', 'lake', 'pond', 'waterfall', 'volcano', 'desert', 'island', 'rainbow', 'thunder', 'lightning', 'snow', 'ice', 'fire', 'water', 'earth', 'air', 'food'],
  grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  // Subject mapping with mu (menu index) values for navigation
  // mu values match the academicSubjects array index in filterData.js
  // VERIFIED: Class 1 Maths = /views/academic/class/class-1?main=0&mu=4
  subjects: {
    english: { mu: 0, name: 'English', aliases: ['eng'] },
    hindi: { mu: 1, name: 'Hindi' },
    telugu: { mu: 2, name: 'Telugu' },
    evs: { mu: 3, name: 'EVS', aliases: ['environmental', 'environment'] },
    maths: { mu: 4, name: 'Maths', aliases: ['math', 'mathematics'] },
    gk: { mu: 5, name: 'GK', aliases: ['general knowledge'] },
    computer: { mu: 6, name: 'Computer', aliases: ['computers', 'it'] },
    art: { mu: 7, name: 'Art', aliases: ['drawing', 'painting'] },
    craft: { mu: 8, name: 'Craft', aliases: ['crafts'] },
    stories: { mu: 9, name: 'Stories', aliases: ['story'] },
    charts: { mu: 10, name: 'Charts', aliases: ['chart'] },
    science: { mu: 3, name: 'Science', aliases: ['sci'] }, // Science maps to EVS for lower classes
  },
  // Age to class mapping (Indian school system)
  ageToClass: {
    3: 'nursery',
    4: 'lkg',
    5: 'ukg',
    6: 'class-1',
    7: 'class-2',
    8: 'class-3',
    9: 'class-4',
    10: 'class-5',
    11: 'class-6',
    12: 'class-7',
    13: 'class-8',
    14: 'class-9',
    15: 'class-10',
    16: 'class-10',
  },
  // Class name mapping for URLs
  classNames: {
    'nursery': 'nursery',
    'lkg': 'lkg',
    'ukg': 'ukg',
    '1': 'class-1',
    '2': 'class-2',
    '3': 'class-3',
    '4': 'class-4',
    '5': 'class-5',
    '6': 'class-6',
    '7': 'class-7',
    '8': 'class-8',
    '9': 'class-9',
    '10': 'class-10',
  }
};
// Trending searches
const TRENDING_SEARCHES = [
  'Class 5 Maths', 'Tiger Images', 'Chart Maker', 'Moral Stories', 'MCQ English'
];
const EnhancedSearch = ({ onSearch, variant = 'home' }) => {
  const navigate = useNavigate();
  const reactLocation = useLocation();
  
  // Decode URL parameter immediately
  const getDecodedFromURL = () => {
    try {
      const params = new URLSearchParams(reactLocation.search || window.location.search);
      const textParam = params.get('text');
      if (textParam) {
        // Handle both encoded and already-decoded text
        const decoded = textParam.includes('%') ? decodeURIComponent(textParam) : textParam;
        return decoded.replace(/%20/g, ' ');
      }
    } catch (e) {
      console.error('Decode error:', e);
    }
    return '';
  };
  
  const [searchText, setSearchText] = useState(() => getDecodedFromURL());
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const recognitionRef = useRef(null);
  
  // Update searchText when location changes
  useEffect(() => {
    const decoded = getDecodedFromURL();
    if (decoded) {
      setSearchText(decoded);
    }
  }, [reactLocation]);
  
  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-IN';
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchText(transcript);
        setIsListening(false);
        setIsFocused(true);
      };
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);
  // Handle voice input
  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('Voice recognition is not supported in your browser. Please try Chrome or Edge.');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        setIsFocused(true);
      } catch (error) {
        console.error('Failed to start voice recognition:', error);
        setIsListening(false);
      }
    }
  };
  // Load recent searches from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('myschool-recent-searches') || '[]');
    setRecentSearches(saved.slice(0, 5));
  }, []);
  // Save search to recent
  const saveToRecent = (text) => {
    const saved = JSON.parse(localStorage.getItem('myschool-recent-searches') || '[]');
    const updated = [text, ...saved.filter(s => s !== text)].slice(0, 10);
    localStorage.setItem('myschool-recent-searches', JSON.stringify(updated));
    setRecentSearches(updated.slice(0, 5));
  };
  // State for backend image results
  const [imageResults, setImageResults] = useState([]);
  // Fetch suggestions from backend API
  const fetchBackendSuggestions = async (query) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rest/search/global?query=${encodeURIComponent(query)}&size=6`);
      if (response.ok) {
        const data = await response.json();
        return data.results || [];
      }
    } catch (error) {
      // Silent fail - use local suggestions
    }
    return [];
  };
  // Fetch backend image results when search text changes
  useEffect(() => {
    const query = searchText.toLowerCase().trim();
    if (query.length >= 2) {
      setIsLoading(true);
      fetchBackendSuggestions(query).then((backendResults) => {
        const images = backendResults
          .filter(result => result.type === 'image' && result.path)
          .slice(0, 4)
          .map(result => ({
            type: 'image-result',
            title: result.title || 'Image',
            path: result.path,
            thumbnail: result.thumbnail,
            category: result.category
          }));
        setImageResults(images);
        setIsLoading(false);
      });
    } else {
      setImageResults([]);
    }
  }, [searchText]);
  // Helper function to find subject from query (including aliases)
  const findSubjectFromQuery = (query) => {
    for (const [key, subject] of Object.entries(KNOWLEDGE_BASE.subjects)) {
      if (query.includes(key)) return { key, ...subject };
      if (subject.aliases) {
        for (const alias of subject.aliases) {
          if (query.includes(alias)) return { key, ...subject };
        }
      }
    }
    return null;
  };

  // Generate smart suggestions based on input
  useEffect(() => {
    if (!searchText.trim()) {
      setSuggestions([]);
      return;
    }
    const query = searchText.toLowerCase().trim();
    const newSuggestions = [];
    
    // PRIORITY 1: Check OCRC categories FIRST
    // Direct OCRC category match
    if (OCRC_CATEGORIES[query]) {
      const config = OCRC_CATEGORIES[query];
      newSuggestions.push({
        type: 'ocrc',
        title: `Browse ${query.charAt(0).toUpperCase() + query.slice(1)} in Image Bank`,
        subtitle: 'One Click Resource Center',
        path: `${config.path}?main=2&mu=${config.mu}`,
        icon: <ImageIcon />,
        color: '#4caf50'
      });
    }
    
    // Check if query matches any OCRC category
    for (const [category, config] of Object.entries(OCRC_CATEGORIES)) {
      if ((category.includes(query) || query.includes(category)) && !newSuggestions.some(s => s.path?.includes(config.path))) {
        newSuggestions.push({
          type: 'ocrc',
          title: `Browse ${category.charAt(0).toUpperCase() + category.slice(1)}`,
          subtitle: 'One Click Resource Center',
          path: `${config.path}?main=2&mu=${config.mu}`,
          icon: <ImageIcon />,
          color: '#4caf50'
        });
        if (newSuggestions.filter(s => s.type === 'ocrc').length >= 2) break;
      }
    }
    
    // Check animal keywords → Animals OCRC
    const matchedAnimal = ANIMAL_KEYWORDS_TO_OCRC.find(a => query.includes(a) || a.includes(query));
    if (matchedAnimal && !newSuggestions.some(s => s.path?.includes('animals'))) {
      newSuggestions.push({
        type: 'ocrc',
        title: `Browse Animals (includes ${matchedAnimal})`,
        subtitle: 'One Click Resource Center',
        path: '/views/academic/imagebank/animals?main=2&mu=0',
        icon: <ImageIcon />,
        color: '#4caf50'
      });
    }
    
    // Check bird keywords → Birds OCRC
    const matchedBird = BIRD_KEYWORDS_TO_OCRC.find(b => query.includes(b) || b.includes(query));
    if (matchedBird && !newSuggestions.some(s => s.path?.includes('birds'))) {
      newSuggestions.push({
        type: 'ocrc',
        title: `Browse Birds (includes ${matchedBird})`,
        subtitle: 'One Click Resource Center',
        path: '/views/academic/imagebank/birds?main=2&mu=1',
        icon: <ImageIcon />,
        color: '#4caf50'
      });
    }
    
    // Check insect keywords → Insects OCRC
    const matchedInsect = INSECT_KEYWORDS_TO_OCRC.find(i => query.includes(i) || i.includes(query));
    if (matchedInsect && !newSuggestions.some(s => s.path?.includes('insects'))) {
      newSuggestions.push({
        type: 'ocrc',
        title: `Browse Insects (includes ${matchedInsect})`,
        subtitle: 'One Click Resource Center',
        path: '/views/academic/imagebank/insects?main=2&mu=6',
        icon: <ImageIcon />,
        color: '#4caf50'
      });
    }
    
    // Check for age-based search (e.g., "age 6 resources", "6 year old")
    const ageMatch = query.match(/(?:age|year|years?\s*old)\s*(\d+)/i) || query.match(/(\d+)\s*(?:year|years?\s*old)/i);
    if (ageMatch) {
      const age = parseInt(ageMatch[1]);
      const className = KNOWLEDGE_BASE.ageToClass[age];
      if (className) {
        const displayClass = className.replace('class-', 'Class ').replace('lkg', 'LKG').replace('ukg', 'UKG').replace('nursery', 'Nursery');
        const matchedSubject = findSubjectFromQuery(query);
        if (matchedSubject) {
          newSuggestions.push({
            type: 'academic',
            title: `${displayClass} ${matchedSubject.name} (Age ${age})`,
            subtitle: 'Academic Navigation',
            path: `/views/academic/class/${className}/${matchedSubject.key}`,
            icon: <AcademicIcon />,
            color: '#2196f3'
          });
        } else {
          newSuggestions.push({
            type: 'academic',
            title: `${displayClass} Resources (Age ${age})`,
            subtitle: 'Academic - Class',
            path: `/views/academic/class/${className}`,
            icon: <AcademicIcon />,
            color: '#2196f3'
          });
        }
      }
    }
    
    // Check for grade + subject pattern (e.g., "class 5 maths", "class 1 maths")
    const gradeMatch = query.match(/(?:class|grade|standard)\s*(\d+)/i);
    const matchedSubject = findSubjectFromQuery(query);
    
    if (gradeMatch) {
      const gradeNum = parseInt(gradeMatch[1]);
      if (KNOWLEDGE_BASE.grades.includes(gradeNum)) {
        const className = `class-${gradeNum}`;
        if (matchedSubject) {
          // URL format: /views/academic/class/class-1?main=0&mu=4
          newSuggestions.push({
            type: 'academic',
            title: `Class ${gradeNum} ${matchedSubject.name}`,
            subtitle: 'Academic Navigation',
            path: `/views/academic/class/${className}?main=0&mu=${matchedSubject.mu}`,
            icon: <AcademicIcon />,
            color: '#2196f3'
          });
        } else {
          // Just class without subject - go to class page
          newSuggestions.push({
            type: 'academic',
            title: `Class ${gradeNum} Resources`,
            subtitle: 'Academic Navigation',
            path: `/views/academic/class/${className}`,
            icon: <AcademicIcon />,
            color: '#2196f3'
          });
        }
      }
    }
    
    // Check for kindergarten classes (nursery, lkg, ukg)
    const kinderMatch = query.match(/\b(nursery|lkg|ukg)\b/i);
    if (kinderMatch) {
      const kinderClass = kinderMatch[1].toLowerCase();
      const displayName = kinderClass === 'lkg' ? 'LKG' : kinderClass === 'ukg' ? 'UKG' : 'Nursery';
      if (matchedSubject) {
        newSuggestions.push({
          type: 'academic',
          title: `${displayName} ${matchedSubject.name}`,
          subtitle: 'Academic Navigation',
          path: `/views/academic/class/${kinderClass}?main=0&mu=${matchedSubject.mu}`,
          icon: <AcademicIcon />,
          color: '#2196f3'
        });
      } else {
        newSuggestions.push({
          type: 'academic',
          title: `${displayName} Resources`,
          subtitle: 'Academic Navigation',
          path: `/views/academic/class/${kinderClass}`,
          icon: <AcademicIcon />,
          color: '#2196f3'
        });
      }
    }
    
    // Check for subject-only match (e.g., "math", "science", "english")
    if (!gradeMatch && !kinderMatch && matchedSubject) {
      // Add suggestions for popular classes with this subject
      [1, 3, 5, 8].forEach(classNum => {
        newSuggestions.push({
          type: 'academic',
          title: `Class ${classNum} ${matchedSubject.name}`,
          subtitle: 'Academic Navigation',
          path: `/views/academic/class/class-${classNum}?main=0&mu=${matchedSubject.mu}`,
          icon: <AcademicIcon />,
          color: '#2196f3'
        });
      });
    }
    // Check for object keywords (for text search fallback)
    const matchedObject = KNOWLEDGE_BASE.objectKeywords.find(k => k.includes(query) || query.includes(k));
    if (matchedObject && newSuggestions.length < 4) {
      newSuggestions.push({
        type: 'imagebank',
        title: `Search Images: "${searchText}"`,
        path: `/views/result?text=${encodeURIComponent(searchText)}`,
        icon: <ImageIcon />,
        color: '#4caf50'
      });
    }
    // Check quick links (partial matching)
    KNOWLEDGE_BASE.quickLinks.forEach(link => {
      if (link.name.toLowerCase().includes(query) || query.split(' ').some(word => link.name.toLowerCase().includes(word) && word.length >= 2)) {
        newSuggestions.push({
          type: 'quicklink',
          title: link.name,
          path: link.path,
          category: link.category,
          icon: link.category === 'Makers' ? <MakerIcon /> : <CategoryIcon />,
          color: link.category === 'Makers' ? '#ff9800' : '#9c27b0'
        });
      }
    });
    // Check category keywords for matching (e.g., "academic", "maker")
    KNOWLEDGE_BASE.categories.forEach(cat => {
      const catMatches = cat.name.toLowerCase().includes(query) || 
                         cat.keywords.some(kw => kw.includes(query) || query.includes(kw));
      if (catMatches && !newSuggestions.some(s => s.path === cat.path)) {
        newSuggestions.push({
          type: 'category',
          title: `Browse ${cat.name}`,
          path: cat.path,
          icon: cat.icon,
          color: cat.color
        });
      }
    });
    // Always add general search option when there's text (at least 2 chars)
    if (searchText.length >= 2) {
      newSuggestions.push({
        type: 'search',
        title: `Search for "${searchText}"`,
        path: `/views/result?text=${encodeURIComponent(searchText)}`,
        icon: <SearchIcon />,
        color: '#666'
      });
    }
    // Remove duplicates based on path and limit to 6
    const uniqueSuggestions = newSuggestions.filter((item, index, self) => 
      index === self.findIndex(t => t.path === item.path)
    );
    setSuggestions(uniqueSuggestions.slice(0, 6));
  }, [searchText]);
  // Handle search submission
  const handleSearch = (path) => {
    if (searchText.trim()) {
      saveToRecent(searchText.trim());
    }
    setIsFocused(false);
    setSearchText('');
    if (path) {
      navigate(path);
    } else if (searchText.trim()) {
      navigate({ pathname: '/views/result', search: `?text=${searchText}` });
    }
  };
  
  // Parse search text to create smart navigation path
  // PRIORITY: 1) Exact OCRC category match only 2) Academic class/subject 3) Text search for everything else
  const getSmartNavigationPath = (text) => {
    const query = text.toLowerCase().trim();
    
    // PRIORITY 1: Check One Click Resource Center (OCRC) categories - EXACT MATCH ONLY
    // Only redirect to OCRC for exact category terms like "animals", "birds", "flowers"
    // NOT for specific items like "monkey", "lion", "parrot" - those should go to search results
    if (OCRC_CATEGORIES[query]) {
      return `${OCRC_CATEGORIES[query].path}?main=2&mu=${OCRC_CATEGORIES[query].mu}`;
    }
    
    // REMOVED: Fuzzy matching that was incorrectly redirecting "monkey" to "animals" page
    // Specific animal/bird/insect keywords should go to search results, not category pages
    
    // PRIORITY 2: Check for age-based search (e.g., "age 6 resources", "age 8")
    // Age mapping: Age 6 = Class 1, Age 7 = Class 2, Age 8 = Class 3, etc.
    const ageMatch = query.match(/(?:age|year|years?\s*old)\s*(\d+)/i) || query.match(/(\d+)\s*(?:year|years?\s*old)/i);
    if (ageMatch) {
      const age = parseInt(ageMatch[1]);
      const className = KNOWLEDGE_BASE.ageToClass[age];
      if (className) {
        // Check if there's also a subject in the query
        const matchedSubject = findSubjectFromQuery(query);
        if (matchedSubject) {
          // URL format: /views/academic/class/class-1?main=0&mu=4 (mu from subject)
          return `/views/academic/class/${className}?main=0&mu=${matchedSubject.mu}`;
        }
        // Just class without subject - go to class page
        return `/views/academic/class/${className}`;
      }
    }
    
    // PRIORITY 3: Check for class + subject pattern (e.g., "class 5 maths", "class 1 english")
    const gradeMatch = query.match(/(?:class|grade|standard)\s*(\d+)/i);
    const matchedSubject = findSubjectFromQuery(query);
    
    if (gradeMatch) {
      const gradeNum = parseInt(gradeMatch[1]);
      if (KNOWLEDGE_BASE.grades.includes(gradeNum)) {
        const className = `class-${gradeNum}`;
        if (matchedSubject) {
          // URL format: /views/academic/class/class-1?main=0&mu=4 (mu from subject)
          return `/views/academic/class/${className}?main=0&mu=${matchedSubject.mu}`;
        } else {
          // Just class without subject - go to class page
          return `/views/academic/class/${className}`;
        }
      }
    }
    
    // Check for kindergarten classes (nursery, lkg, ukg)
    const kinderMatch = query.match(/\b(nursery|lkg|ukg)\b/i);
    if (kinderMatch) {
      const kinderClass = kinderMatch[1].toLowerCase();
      if (matchedSubject) {
        // URL format: /views/academic/class/nursery?main=0&mu=4
        return `/views/academic/class/${kinderClass}?main=0&mu=${matchedSubject.mu}`;
      }
      return `/views/academic/class/${kinderClass}`;
    }
    
    // PRIORITY 4: Default to text-based search for all other queries
    // This includes specific items like "monkey", "lion", "parrot", etc.
    return `/views/result?text=${encodeURIComponent(text.trim())}`;
  };
  
  // Handle keyboard
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchText.trim()) {
        // Use smart navigation based on the search text
        const smartPath = getSmartNavigationPath(searchText);
        handleSearch(smartPath);
      }
    }
    if (e.key === 'Escape') {
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && 
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const showDropdown = isFocused && (searchText.length > 0 || recentSearches.length > 0);
  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: variant === 'home' ? 600 : 400 }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder={isListening ? "Listening... Speak now" : "Search your wish here..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
        sx={{
          '.MuiOutlinedInput-root': {
            borderRadius: '28px',
            backgroundColor: '#fff',
            boxShadow: isFocused ? '0 4px 20px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            },
            '& fieldset': {
              border: 'none',
            }
          },
          '.MuiInputBase-input': {
            py: 1.5,
            px: 2,
            fontSize: variant === 'home' ? '1.1rem' : '0.95rem',
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#1976d2', ml: 1 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ gap: 0.5 }}>
              {searchText && (
                <IconButton size="small" onClick={() => setSearchText('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton 
                size="small" 
                onClick={handleVoiceInput}
                sx={{ 
                  color: isListening ? '#f44336' : '#666',
                  animation: isListening ? 'pulse 1.5s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)' },
                  }
                }}
                title={isListening ? 'Listening... Click to stop' : 'Click to speak'}
              >
                <MicIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      {/* Dropdown */}
      <Fade in={showDropdown}>
        <Paper
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxHeight: 450,
            overflow: 'auto'
          }}
        >
          {/* Image Results from Backend */}
          {imageResults.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Found Images
                </Typography>
              </Box>
              <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {imageResults.map((img, idx) => (
                  <SearchImageThumbnail
                    key={idx}
                    img={img}
                    onClick={() => handleSearch(`/views/result?text=${encodeURIComponent(searchText)}`)}
                  />
                ))}
              </Box>
              <Divider />
            </>
          )}
          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {/* Suggestions */}
          {suggestions.length > 0 && (
            <>
              <List dense sx={{ py: 1 }}>
                {suggestions.map((suggestion, idx) => (
                  <ListItem
                    key={idx}
                    button
                    onClick={() => handleSearch(suggestion.path)}
                    sx={{ 
                      py: 1.5,
                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: suggestion.color }}>
                      {suggestion.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={suggestion.title}
                      secondary={suggestion.category}
                      primaryTypographyProps={{ fontSize: '0.95rem' }}
                    />
                    <ArrowIcon sx={{ color: '#999', fontSize: 18 }} />
                  </ListItem>
                ))}
              </List>
              <Divider />
            </>
          )}
          {/* Recent Searches - shown when no input */}
          {!searchText && recentSearches.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Recent Searches
                </Typography>
              </Box>
              <List dense sx={{ py: 0 }}>
                {recentSearches.map((search, idx) => (
                  <ListItem
                    key={idx}
                    button
                    onClick={() => {
                      setSearchText(search);
                      handleSearch(`/views/result?text=${encodeURIComponent(search)}`);
                    }}
                    sx={{ py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <HistoryIcon fontSize="small" sx={{ color: '#999' }} />
                    </ListItemIcon>
                    <ListItemText primary={search} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                  </ListItem>
                ))}
              </List>
              <Divider />
            </>
          )}
          {/* Trending */}
          {!searchText && (
            <>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Trending Searches
                </Typography>
              </Box>
              <Box sx={{ px: 2, pb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {TRENDING_SEARCHES.map((term, idx) => (
                  <Chip
                    key={idx}
                    label={term}
                    size="small"
                    icon={<TrendingIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      setSearchText(term);
                      handleSearch(`/views/result?text=${encodeURIComponent(term)}`);
                    }}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  />
                ))}
              </Box>
            </>
          )}
          {/* Quick Categories - shown when no input */}
          {!searchText && (
            <>
              <Divider />
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Browse by Category
                </Typography>
              </Box>
              <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {KNOWLEDGE_BASE.categories.map((cat, idx) => (
                  <Chip
                    key={idx}
                    label={cat.name}
                    size="small"
                    icon={cat.icon}
                    onClick={() => navigate(cat.path)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: `${cat.color}15`,
                      color: cat.color,
                      '&:hover': { backgroundColor: `${cat.color}25` },
                      '& .MuiChip-icon': { color: cat.color }
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Paper>
      </Fade>
    </Box>
  );
};
export default EnhancedSearch;
